import { getImageData, IUrlBuilderArgs } from 'gatsby-plugin-image';

import { IImage, IMdBody, Tag } from '../../../types/shared';

import {
  ApolloImage,
  ArticlePageData,
  ArticleQueryData,
} from './ArticlePage.types';

export const parseArticleTags = (data: ArticlePageData) => {
  if (data) {
    const tagsArray: Tag[] = [];

    data.tags?.forEach((tag) => {
      tagsArray.push({ ...(tag as Tag) });
    });

    // Make sure only unique tagnames
    const tags: Tag[] = [...new Set(tagsArray)];
    const allTagNames: string[] = [
      ...new Set(tagsArray.map((tag) => tag.name)),
    ];

    return [tags, allTagNames];
  }

  return [null, []];
};

// TODO: Make separate / shared parse related helper file for all image related stuff.
const urlBuilder = ({
  baseUrl,
  width,
  height,
  format,
}: IUrlBuilderArgs<'auto'>): string =>
  `${baseUrl}?w=${width}&h=${height}&q=80&fm=${format}`;

enum ImageBreakpoints {
  S = 750,
  M = 1080,
  L = 1366,
  XL = 1920,
}

// TODO: Effectively this helper only supports layout: fullWidth still.
const parseQueryImage = (image: ApolloImage): IImage => ({
  gatsbyImageData: getImageData({
    baseUrl: image.url,
    width: image.width,
    height: image.height,
    formats: ['auto', 'webp'],
    aspectRatio: image.width / image.height,
    breakpoints: [
      ImageBreakpoints.S,
      ImageBreakpoints.M,
      ImageBreakpoints.L,
      ImageBreakpoints.XL,
    ],
    layout: 'fullWidth',
    urlBuilder,
  }),
  description: image.description,
});

const parseImage = (image: IImage, queryImage: ApolloImage): IImage =>
  queryImage ? parseQueryImage(queryImage) : image;

const parseTags = (
  tags: ArticlePageData['tags'],
  queryTags: ArticleQueryData['article']['tagsCollection']
): Tag[] =>
  queryTags.items
    ? queryTags.items.map(({ _id, __typename, ...tag }) => ({
        id: _id,
        ...tag,
      }))
    : tags;

const parseMarkdownBody = (body: string): IMdBody => ({
  childMarkdownRemark: {
    rawMarkdownBody: body,
  },
});

const parseArticleBody = (
  articleBody: ArticlePageData['articleBody'],
  queryArticleBody: ArticleQueryData['article']['articleBodyCollection']['items']
): ArticlePageData['articleBody'] => {
  if (queryArticleBody) {
    return queryArticleBody.map(
      ({ _id, __typename: typeName, body, image, ...item }) => ({
        ...item,
        __typename:
          typeName === 'ArticleParagraph'
            ? 'ContentfulArticleTextSection'
            : 'SomeOtherType',
        id: _id,
        body: parseMarkdownBody(body),
        image: image ? parseQueryImage(image) : undefined,
      })
    );
  }

  return articleBody;
};

export const parsePageData = (
  pageData: ArticlePageData,
  queryData: ArticleQueryData
): ArticlePageData => {
  const title = queryData.article.title || pageData.title;
  const mainImage = parseImage(pageData.mainImage, queryData.article.mainImage);
  const tags = parseTags(pageData.tags, queryData.article.tagsCollection);
  const articleBody = parseArticleBody(
    pageData.articleBody,
    queryData.article.articleBodyCollection.items
  );

  return {
    ...pageData,
    title,
    mainImage,
    tags,
    publishingTime: queryData.article.publishingTime || pageData.publishingTime,
    lastContentUpdate:
      queryData.article.lastContentUpdate || pageData.lastContentUpdate,
    readingMinutes: queryData.article.readingMinutes || pageData.readingMinutes,
    articleBody,
  };
};
